import { MIAuth } from '../auth/MIAuth';
import { MIDiagnostics } from '../diagnostics/MIDiagnostics';

/**
 * Typed client for the MIGIS analysis REST API. Wraps the server-side
 * /api/v2/analysis/* endpoints (flood, viewshed, best-path, ballistic-6dof,
 * arc-viewshed, rainfall-flood, etc.) so consumers do not write fetch by hand.
 */
export interface MIAnalysisClientOptions {
  /** Base URL for the analysis API. Defaults to same-origin. */
  baseUrl?: string;
  /** Custom fetch (for testing or proxies). */
  fetchImpl?: typeof fetch;
  /** Number of retry attempts on 5xx / network errors. */
  retries?: number;
}

async function _post<TReq, TRes>(
  url: string, body: TReq, opts: MIAnalysisClientOptions
): Promise<TRes> {
  const f = opts.fetchImpl ?? fetch;
  const retries = opts.retries ?? 1;
  let lastErr: any;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await f(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...MIAuth.getAuthHeaders(),
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401 && attempt === 0) {
        // Try one re-auth + retry
        try { await MIAuth.authenticate(); } catch {}
        continue;
      }
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Analysis ${url} failed (${res.status}): ${text}`);
      }
      return await res.json() as TRes;
    } catch (err) {
      lastErr = err;
      MIDiagnostics.warn('MIAnalysisClient', `attempt ${attempt + 1} failed for ${url}`,
        err instanceof Error ? err : new Error(String(err)));
      if (attempt === retries) break;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

export class MIAnalysisClient {
  private opts: MIAnalysisClientOptions;
  constructor(options: MIAnalysisClientOptions = {}) { this.opts = options; }

  private url(path: string): string {
    const base = this.opts.baseUrl ?? '';
    return `${base}/api/v2/analysis/${path.replace(/^\//, '')}`;
  }

  /** POST /api/v2/elevation/query */
  async elevation(input: { points: Array<[number, number]> }): Promise<{ elevations: number[] }> {
    return _post(this.url('../elevation/query'), input, this.opts);
  }

  /** POST /api/v2/elevation/profile */
  async elevationProfile(input: { line: Array<[number, number]>; samples?: number }): Promise<any> {
    return _post(this.url('../elevation/profile'), input, this.opts);
  }

  flood(input: any): Promise<any> { return _post(this.url('flood'), input, this.opts); }
  bestPath(input: any): Promise<any> { return _post(this.url('bestpath'), input, this.opts); }
  viewshed(input: any): Promise<any> { return _post(this.url('viewshed'), input, this.opts); }
  lineOfSight(input: any): Promise<any> { return _post(this.url('line-of-sight'), input, this.opts); }
  volumeComparison(input: any): Promise<any> { return _post(this.url('volume'), input, this.opts); }
  mobility(input: any): Promise<any> { return _post(this.url('mobility'), input, this.opts); }

  // §24 gap-closure endpoints
  arcViewshed(input: any): Promise<any> { return _post(this.url('arc-viewshed'), input, this.opts); }
  rainfallFlood(input: any): Promise<any> { return _post(this.url('rainfall-flood'), input, this.opts); }
  ballistic6DOF(input: any): Promise<any> { return _post(this.url('ballistic-6dof'), input, this.opts); }
  bestPathSlope(input: any): Promise<any> { return _post(this.url('best-path-slope'), input, this.opts); }
}
